import { FunctionComponent } from 'react'
import {
  ChecklistDocumentFilter,
  ChecklistDocumentSummary,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  rucksack,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@black-bear-energy/rucksack'
import { DocumentFilterCounts } from '../helpers/DocumentExchange/DocumentFilterCounts'
import houseSearch from '../../assets/house-search.svg'
import { SearchBarAndFilterTabs } from './SearchBarAndFilterTabs'
import { ChecklistSection } from './ChecklistSection'
import { Surface } from '../common/Surface'
import {
  CHECKLIST_TABLE_WIDTH,
  CHECKLIST_COLUMN_WIDTHS,
} from '../helpers/DocumentExchange/tableConstants'

export const ChecklistTable: FunctionComponent<ChecklistTableProps> = ({
  checklists,
  counts,
  isPending,
  search,
  setSearch,
  filter,
  setFilter,
}) => {
  const defaultTableHeight = '45.25rem'

  return (
    <Surface>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '& .MuiTableCell-root': { padding: '8px' },
        }}
      >
        <SearchBarAndFilterTabs
          searchBarLabel={'Search by checklist, document type, or "updated by"'}
          counts={counts}
          search={search}
          setSearch={setSearch}
          filter={filter}
          setFilter={setFilter}
        />
        {isPending && (
          <Skeleton variant="rounded" height={defaultTableHeight} />
        )}
        {!isPending && (
          <>
            {!(checklists && checklists.length > 0) ? (
              <Box
                sx={{
                  width: '100%',
                  height: defaultTableHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2.5,
                  py: 5,
                }}
              >
                <img src={houseSearch} alt="" />
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  No results found.
                  <br />
                  Please try a new search.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ minHeight: defaultTableHeight }}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: rucksack.palette.divider,
                    borderRadius: rucksack.shape.borderRadius,
                    overflowX: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        tableLayout: 'fixed',
                        width: CHECKLIST_TABLE_WIDTH,
                        minWidth: '1000px',
                      }}
                    >
                      <colgroup>
                        {CHECKLIST_COLUMN_WIDTHS.map((width, index) => (
                          <col style={{ width }} key={index} />
                        ))}
                      </colgroup>
                      <TableBody
                        sx={{
                          '& > .MuiTableRow-root:last-child > .MuiTableCell-root':
                            {
                              borderBottom: 'none',
                            },
                        }}
                      >
                        {checklists.map((checklist, index) => (
                          <ChecklistSection
                            key={checklist.checklistName}
                            checklistSummary={checklist}
                            isLast={index === checklists.length - 1}
                            searchString={search}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Surface>
  )
}

export interface ChecklistTableProps {
  checklists?: ChecklistDocumentSummary[]
  counts: DocumentFilterCounts
  isPending: boolean
  search: string
  setSearch: (search: string) => void
  filter: ChecklistDocumentFilter
  setFilter: (filter: ChecklistDocumentFilter) => void
}
